import colorPallete, { bodyFonts, buttonFonts } from "@DSG/MobiusOverrides/styles/variables";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    messageText?: TypographyPresentationProps;
    cookieButtonsWrapper: InjectableCss;
    acceptButton?: ButtonPresentationProps;
    closeButton?: ButtonPresentationProps;
}

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        cssOverrides: {
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                padding: 24px;
                margin: 0 auto;
                width: 100%;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            padding: 16px;
                        `,
                        null,
                    ], "max")}
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    container: {
        gap: 10,
        offsetCss: css`
            align-items: center;
            justify-content: space-between;
        `,
        css: css`
            margin: 0 auto;
            > ${GridOffset} {
                width: 100%;
                margin: 0;
                row-gap: 16px;

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-wrap: nowrap;
                        row-gap: 0;
                        column-gap: 24px;
                    `,
                    null,
                ], "min")}
            }
        `,
    },
    messageText: {
        size: bodyFonts.body2RegularLg.fontSize,
        lineHeight: bodyFonts.body2RegularLg.lineHeight,
        weight: bodyFonts.body2RegularLg.fontWeight,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    &, & span {
                        font-size: ${bodyFonts.body2RegularSm.fontSize} !important;
                        line-height: ${bodyFonts.body2RegularSm.lineHeight};
                    }
                `,
                null,
            ], "max")}
        `
    },
    cookieButtonsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            column-gap: 16px;

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    flex-direction: column;
                    width: 100%;
                    row-gap: 16px;
                    column-gap: 0;
                `,
                null,
            ], "max")}
        `
    },
    acceptButton: {
        css: css`
            width: 100%;
            padding: 14px 24px;
            min-width: 175px;
        `,
    },
    closeButton: {
        color: colorPallete.primaryRed500,
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            padding: 14px 32px;
            min-width: 138px;
            background: ${colorPallete.white};
            font-size: ${buttonFonts.buttonLg.fontSize};
            line-height: ${buttonFonts.buttonLg.lineHeight};
            font-weight: ${buttonFonts.buttonLg.fontWeight};

            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    font-size: ${buttonFonts.buttonSm.fontSize};
                    line-height: ${buttonFonts.buttonSm.lineHeight};
                `,
                null,
            ], "max")}

            ${IconWrapper} {
                margin-left: 4px;
            }
        `,
        variant: "secondary",
    },
};

const styles = cookiePrivacyPolicyPopupStyles;

const CookiePrivacyPolicyPopup = () => {
    const location = useAppSelector(o => getLocation(o));
    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie("acceptCookies")) {
            setIsOpen(true);
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setIsOpen(false);
        setCookie("acceptCookies", "true", { expires: 365 });
    };

    const closeHandler = () => {
        setIsOpen(false);
    };

    return (
        <Drawer isOpen={isOpen} {...styles.drawer} data-test-selector="drawer_CookiePolicy">
            <GridContainer {...styles.container}>
                <Typography {...styles.messageText} data-test-selector="cookiePolicy_PrivacyPolicy">
                    {siteMessage("Core_CookiePrivacyPolicy")}
                </Typography>
                <StyledWrapper {...styles.cookieButtonsWrapper}>
                    <Button
                        {...styles.acceptButton}
                        onClick={acceptHandler}
                        data-test-selector="cookiePolicy_AcceptCookies"
                    >
                        {siteMessage("Core_CookiePrivacyPolicyAccept")}
                    </Button>
                    <Button
                        {...styles.closeButton}
                        onClick={closeHandler}
                        data-test-selector="cookiePolicy_ClosePolicy"
                    >
                        {
                            translate("Close")
                        }
                        <ButtonIcon src={X} />
                    </Button>
                </StyledWrapper>
            </GridContainer>
        </Drawer>
    );
};

export default CookiePrivacyPolicyPopup;